import React, { useMemo, useState } from 'react';
import SearchableDropdown from 'src/components/AtlasV2Components/Common/SearchableDropdown';
import { GroupByField } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficDropdownTopEntityTraffic';

export interface UseSalesCustomGroupByArgs {
  enabled: boolean;
  groupByFields: GroupByField[];
}

export interface UseSalesCustomGroupByResult {
  computedGroupByFields: GroupByField[];
  selectedGroupByField?: string;
  additionalParams?: Record<string, any>;
  renderGroupBySelector?: (args: {
    options: { id: string; label: string }[];
    selectedId: string;
    onChange: (id: string) => void;
  }) => React.ReactNode;
}

export const useSalesCustomGroupBy = ({
  enabled,
  groupByFields
}: UseSalesCustomGroupByArgs): UseSalesCustomGroupByResult => {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isCustomMode, setIsCustomMode] = useState(false);

  const computedGroupByFields = useMemo(() => {
    if (!enabled) return groupByFields;
    if (isCustomMode && selectedTag) {
      return [
        ...groupByFields,
        {
          id: 'productAttributeValue',
          label: selectedTag,
          indexName: 'sales'
        } as unknown as GroupByField
      ];
    }
    return groupByFields;
  }, [enabled, groupByFields, isCustomMode, selectedTag]);

  const additionalParams = useMemo(() => {
    if (!enabled) return undefined;
    return isCustomMode && selectedTag ? { productTags: [selectedTag] } : undefined;
  }, [enabled, isCustomMode, selectedTag]);

  const selectedGroupByField = useMemo(() => {
    if (!enabled) return undefined;
    return isCustomMode ? 'productAttributeValue' : undefined;
  }, [enabled, isCustomMode]);

  const renderGroupBySelector = enabled
    ? ({
        options,
        selectedId,
        onChange
      }: {
        options: { id: string; label: string }[];
        selectedId: string;
        onChange: (id: string) => void;
      }) => {
        // Hide the synthetic 'productAttributeValue' from STANDARD list; it belongs under CUSTOM
        const displayOptions = options.filter((o) => o.id !== 'productAttributeValue');
        return (
          <SearchableDropdown
            selectedId={selectedId}
            onChange={(opt) => {
              setIsCustomMode(false);
              onChange(opt.id);
            }}
            onCustomSelect={(tag) => {
              setSelectedTag(tag);
              setIsCustomMode(true);
            }}
            selectedCustomTag={selectedTag || undefined}
            defaultLabel=""
            buttonVariant="title"
            standardOptions={displayOptions as any}
            enableCustom
            minMenuWidthPx={144}
          />
        );
      }
    : undefined;

  return {
    computedGroupByFields,
    selectedGroupByField,
    additionalParams,
    renderGroupBySelector
  };
};
